"use client";

import { Moon, Sun } from "lucide-react";
import { useSyncExternalStore } from "react";
import { useUi } from "@/components/ui/use-ui";
import { cn } from "@/lib/cn";
import { applyTheme, currentTheme, subscribeTheme, type Theme } from "@/lib/theme";

// The menu's switch between Cherry Night (dark) and Sweet Blush (light), lib/theme.ts. Until the visitor picks
// one, the site follows the device, live (a phone that turns dark at night takes the site along).

export function ThemeToggle({ className }: { className?: string }) {
  const t = useUi();
  const theme = useSyncExternalStore(subscribeTheme, currentTheme, () => null);

  const option = (value: Theme, label: string, Icon: typeof Sun) => (
    <button
      type="button"
      onClick={() => applyTheme(value, true)}
      aria-pressed={theme === value}
      data-stat={`theme:${value}`}
      className={cn(
        "flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 transition-colors",
        theme === value ? "bg-brand-strong text-white" : "text-muted hover:text-fg",
      )}
    >
      <Icon size={14} strokeWidth={2} aria-hidden />
      {label}
    </button>
  );

  return (
    <div role="group" aria-label={t.nav.theme} className={cn("flex rounded-full border border-line bg-surface p-0.5 text-[12px] font-medium", className)}>
      {option("dark", t.nav.themeDark, Moon)}
      {option("light", t.nav.themeLight, Sun)}
    </div>
  );
}
